import { BasemapLayer, Basemaps, basemapLayer } from "esri-leaflet";
import React, { useEffect, useRef, useState } from "react";
import {
  Dropdown,
  DropdownItemAnchor,
  DropdownMenu,
  DropdownTrigger,
  Icon,
  bulma,
} from "trunx";

import { faAngleDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ReactDOM from "react-dom";
import { useMap } from "react-leaflet";
import config from "./config";
import { useLocaleContext } from "./contexts/LocaleContext";

const baseMaps: Basemaps[] = [
  "Streets",
  "Imagery",
  "ImageryLabels",
  "Topographic",
  "TerrainLabels",
  "ShadedRelief",
];

const baseMapsToShow = (layer: Basemaps): Basemaps[] => {
  switch (layer) {
    case "ImageryLabels":
      return [layer, "Imagery"];
    case "TerrainLabels":
      return [layer, "Terrain"];
    default:
      return [layer];
  }
};

const MapStyleDropdown = (): JSX.Element => {
  const container = React.useMemo(() => document.createElement("div"), []);

  const className = "map-style-dropdown";

  React.useEffect(() => {
    const previous = document.querySelector(
      ".leaflet-control-container " + className
    );
    if (previous) {
      previous.remove();
    }

    const root = document.querySelector(".leaflet-top.leaflet-right");
    if (root) {
      container.className = className;
      root.prepend(container);
    }
  }, [container]);

  const [selectBase, setSelectBase] = useState<boolean>(false);

  const map = useMap();
  const prevKey = useRef<string>();
  const [currentLayers, setLayers] = useState<BasemapLayer[]>([]);
  const [baseMap, setBaseMap] = useState<Basemaps>(config.basemap);

  const { classList } = map.getContainer();
  const darkerMap = baseMap.startsWith("Imagery")
  classList[darkerMap ? "add" : "remove"]("darker-map")

  useEffect(() => {
    if (map && prevKey.current !== baseMap) {
      currentLayers.forEach((l) => map.removeLayer(l));
      const layers = baseMapsToShow(baseMap).map((m) => basemapLayer(m));
      layers.map((l) => l.addTo(map));
      setLayers(layers);
      prevKey.current = baseMap;
    }
  }, [baseMap, currentLayers, map]);

  const {
    dict: {
      panel: { layers: dict },
    },
  } = useLocaleContext();

  const baseLayerItems = baseMaps.map((key) => (
    <DropdownItemAnchor
      key={key}
      isActive={key === baseMap}
      onClick={() => {
        setBaseMap(key);
        setSelectBase(false);
      }}
    >
      {dict.baseMapOptions[key]}
    </DropdownItemAnchor>
  ));

  const node = (
    <Dropdown
      isActive={selectBase}
      className={bulma(["is-hidden-touch", "is-right"]) + " leaflet-control"}
    >
      <DropdownTrigger onClick={() => setSelectBase(!selectBase)}>
        <span>{dict.baseMapOptions[baseMap]}</span>
        <Icon className={bulma("is-small")}>
          <FontAwesomeIcon icon={faAngleDown} />
        </Icon>
      </DropdownTrigger>
      <DropdownMenu>
        {baseLayerItems}
      </DropdownMenu>
    </Dropdown>
  );

  return ReactDOM.createPortal(node, container);
};

export default MapStyleDropdown;
